import { useState } from 'react';
import { ApprovalReason, standingOf } from './ApprovalReason';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import {
  line, Color, EDIT_FIELDS, LIST_PAGES, Size, bannerPills, heldDraftPill, pageKindLineText, t,
  type BannerFact, type BannerPill, type DiscountLevel, type DocumentFileRow, type QuotePrimaryAction,
  type PrintSheetHtml, type PrintSheetLayout, type QuoteAcceptanceView,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { QuoteBomChangeLine } from './BomCostChange';
import { QuoteFloorLine, type QuoteFloorView } from './QuoteFloor';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { AccessDenied } from '../../shell/Fallbacks';
import type { ShellOutletContext } from '../../shell/AppShell';
import { PAGES_PATH, useApi } from './api';
import { DocumentFilesPanel } from './DocumentFilesPanel';
import { DocumentHistoryPanel } from './DocumentHistoryPanel';
import { RecordMessagesPanel } from '../mail/RecordMessagesPanel';
import { DocumentNotesPanel } from './DocumentNotesPanel';
import { BannerPills } from './BannerPills';
import { DocumentViewPage } from './DocumentViewPage';
import { documentPdfPath } from './ViewPdfLink';
import { EditRecordModal } from './EditRecordModal';
import { QUOTES_PATH, QuoteAcceptanceLine, QuoteActions, QuoteLines, QuoteVersions, quotePrintDocument, type QuoteLineView, type QuoteVersionView } from './QuotePanels';
import { DOCUMENT_SIGNATURES_PATH, RecordSignedCopyModal, SignaturesPanel, type SignatureRowView } from './RecordSignedCopyModal';
import type { TileAnswer, TileRow } from './tileItems';
import { openFloorsOf } from '../approvals/FloorOverrideWindow';
import styles from './DocumentPage.module.css';
import { DealTeamPanel } from './DealTeamPanel';
import type { DocumentDeliveryView, FrozenDealTeam, LeftInZohoFile, QuoteBomChange, TaxJurisdictionTotal, WhatsNextStep } from '@bms2/shared';
import { DeliveredPanel } from './DeliveredPanel';

/**
 * 🔒 The Quote's own page (decision 0470): the fourth accounting document to
 * read the way the Purchase Order, the Sales Order and the Invoice read — the
 * banner with the number as its title and *Quote* under it, the printed sheet,
 * the lines with the money beneath them, and the panels stacked under that.
 * 🔨 It is not the Tile Canvas: nothing here is dragged, and the order of the
 * blocks is the document's, fixed.
 *
 * 🔨 Internal Notes and History stay, as their own panels (decision 0470).
 * Losing a Quote's notes or its audit trail would be a change to what a person
 * can do, which is not what a layout change is allowed to cost.
 *
 * The page reads the record-page answer the Quote's service already builds, so
 * this is where the Quote is drawn and not what it does.
 */
export interface QuotePageAnswer {
  /** 🔒 The Deal's team as it stood when the quote was created (decision 0765). */
  dealTeam?: FrozenDealTeam | null;
  recordType: 'Quote';
  recordId: string;
  /** The number, which is the banner's title (decision 0459). */
  title: string;
  tiles?: TileAnswer[];
  facts?: Record<string, string | boolean | null>;
  /** What the banner's own Edit opens on: the dates, the terms and the display mode. */
  editable?: Record<string, unknown>;
  /** 🔒 What the Quote is against, drawn at the top of its Details view and never in the title bar (T170). */
  documentFacts?: BannerFact[];
  lines?: QuoteLineView[];
  /** 🔒 The Quote's stored tax rows, one per jurisdiction and one per reduced rate (decisions 0738, 1001). */
  taxRows?: TaxJurisdictionTotal[];
  versions?: QuoteVersionView[];
  /** 🔒 The layout the quote prints on, so view mode positions the regions the sheet positions (decision 0567). */
  printLayout?: PrintSheetLayout | null;
  /** 🔒 The sheet the PDF view draws, as the printer's own HTML, in this same answer (T169). */
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the quote, or null where they may (decision 0687). */
  writeRefusal?: string | null;
  /** 🔒 The newest send's Delivered panel (decisions 1225, 1226); null where nothing was mailed. */
  delivery?: DocumentDeliveryView | null;
  /** 🔒 The date and the threshold the send window warns on, on a Quote awaiting its first send (decision 1227). */
  staleSend?: { documentDate: string; thresholdDays: number } | null;
  /** 🔒 The bill of materials changed after the revision went out, until a later revision is sent (decision 1466); absent from an older reply. */
  bomChange?: QuoteBomChange | null;
  /** 🔒 Whether this viewer may approve the Quote now, the queue's own answer for this one document (T644; decision 1521). */
  canApprove?: boolean;
  /** 🔒 The Quote's total under its Deal's floor, with the reason that covers it (decision 1629); null or absent at or above it. */
  documentFloor?: QuoteFloorView | null;
  /** 🔒 Each line under the floor of the BOM rows it names (T920; decision 1695). */
  lineFloors?: (QuoteFloorView & { lineId: string })[];
  /** 🔒 Who accepted the Quote and the optional lines taken (decision 2650); null before an answer, absent from an older reply. */
  acceptance?: QuoteAcceptanceView | null;
}

/**
 * A Files tile row as the document files panel reads it (decisions 0457, 0470).
 * The tile's row carries the type, the description and the version count joined
 * into one detail line; the panel wants them apart, so they are taken apart
 * here rather than the server answering one file two ways.
 */
export function documentFilesFrom(rows: readonly TileRow[]): DocumentFileRow[] {
  // 🔒 Decision 4805: a file left in Zoho Books is no file here, and is drawn apart by its own list.
  return rows.filter((row) => row.raw?.leftInZoho !== true).map((row) => {
    const parts = (row.detail ?? '').split(' · ').filter((part) => part !== '');
    const typeName = row.value ?? parts[0] ?? '';
    if (parts[0] === typeName) parts.shift();
    const last = parts[parts.length - 1] ?? '';
    const versions = /^v\d+$/.test(last) ? Number(parts.pop()!.slice(1)) : 1;
    return {
      id: row.id ?? '',
      filename: row.label,
      fileTypeName: typeName,
      description: parts.join(' · ') || null,
      versions,
      uploadedAt: row.at ?? '',
      // 🔨 Only Verified is drawn, which is the one the tile draws as a pill too.
      verificationStatus: row.pill === 'Verified' ? 'Verified' : null,
      // 🔒 The served version's stored name, for the Files tab's filter (T1741; decision 2713).
      storedName: typeof row.raw?.storedName === 'string' ? row.raw.storedName : null,
      // 🔒 What the one File row draws, carried off the tile row's own facts (decision 3010).
      quarantine: typeof row.raw?.quarantine === 'string' ? row.raw.quarantine : null,
      mimeType: typeof row.raw?.mimeType === 'string' ? row.raw.mimeType : null,
      restriction: row.flag ? { text: row.flag, group: row.flagGroup === 'warning' ? 'warning' : 'inactive' } : null,
      scanVerdict: row.raw?.scanVerdict === 'Pending' || row.raw?.scanVerdict === 'Clean' || row.raw?.scanVerdict === 'Blocked' ? row.raw.scanVerdict : null,
      scannedAt: typeof row.raw?.scannedAt === 'string' ? row.raw.scannedAt : null,
      scannedBy: typeof row.raw?.scannedBy === 'string' ? row.raw.scannedBy : null,
    };
  });
}

/** 🔒 The files a Quote's Zoho estimate keeps there (decision 4805), carried among its Files rows and marked, each with the estimate's page or none. */
export function leftInZohoFrom(rows: readonly TileRow[]): LeftInZohoFile[] {
  return rows.filter((row) => row.raw?.leftInZoho === true).map((row) => ({ name: row.label, url: typeof row.raw?.url === 'string' ? row.raw.url : null }));
}

/** The rows of one tile in the answer, or none where the answer carries no such tile. */
function rowsOf(answer: QuotePageAnswer, key: string): TileRow[] {
  return answer.tiles?.find((tile) => tile.key === key)?.rows ?? [];
}

export function QuotePage({ quoteId, apiBaseUrl = '', personId = '', timeZone = 'UTC' }: { quoteId: string; apiBaseUrl?: string; personId?: string; timeZone?: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const page = useApi<QuotePageAnswer>(quoteId ? `${PAGES_PATH}/Quote/${quoteId}` : null, apiBaseUrl);
  const [notice, setNotice] = useState<string | null>(null);
  // 🔒 Each press of the What's next banner's step, handed to the footer's actions to open its primary (decision 1732).
  const [primaryRequest, setPrimaryRequest] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  // Piece 27: the signatures on this quote, read beside the page rather than folded into its tile rows.
  const signatures = useApi<{ rows: SignatureRowView[]; electronicEnabled: boolean }>(quoteId ? `${DOCUMENT_SIGNATURES_PATH}?documentType=Quote&documentId=${encodeURIComponent(quoteId)}` : null, apiBaseUrl);
  const [signedCopyOpen, setSignedCopyOpen] = useState(false);

  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const saved = (message: string | null) => {
    setNotice(message);
    page.reload();
  };
  /** 🔨 A refusal reads as one: the banner takes its tone from the leading mark, the way every document page's does. */
  const failed = (message: string) => setNotice(`!${message}`);

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(answer) => {
        const facts = answer.facts ?? {};
        const lines = answer.lines ?? [];
        const pills: BannerPill[] = bannerPills('Quote', facts);
        // 🔒 A held Draft's band reads Pending Approval, as its pill does (T1195; decision 1861).
        const held = typeof facts.displayedStatus === 'string' ? heldDraftPill(facts.displayedStatus, typeof facts.approvalStatus === 'string' ? facts.approvalStatus : null) : null;
        // 🔒 The floors no reason covers yet, off the page's own reads (T1195; decision 1861): Submit warns on them and Approve asks for the reason.
        const openFloors = openFloorsOf(answer);
        const quotePrimary = (typeof facts.primaryAction === 'string' ? facts.primaryAction : null) as QuotePrimaryAction | null;
        // 🔒 The live Sales Order this Quote became, if any (T438; decision 1121).
        const convertedSalesOrder = typeof facts.convertedSalesOrderId === 'string' && typeof facts.convertedSalesOrderNumber === 'string'
          ? { id: facts.convertedSalesOrderId, number: facts.convertedSalesOrderNumber }
          : null;
        // 🔒 The banner's one step is the footer's primary, in the rule table's words (decision 1732).
        const quoteStep: WhatsNextStep | null = quotePrimary === 'send' ? 'send' : quotePrimary === 'markAccepted' ? 'accept' : quotePrimary === 'convert' ? 'convert' : null;
        const footer = (
          <QuoteActions
            quoteId={answer.recordId}
            canApprove={answer.canApprove === true}
            askedAction={params.get('act') === 'send' ? 'send' : null}
            /*
             * 🔒 The server decides which edit this status allows, and the page
             * asks it (decision 0474). Reading `status === 'Draft'` here was why
             * decision 0172's reopen — every change to a sent Quote becomes the
             * next revision — had no route from any screen.
             */
            offersEdit={facts.editable === true}
            reviseAs={typeof facts.nextRevision === 'string' ? facts.nextRevision : null}
            frozenReason={typeof facts.frozenReason === 'string' ? facts.frozenReason : null}
            /* 🔨 Edit stands on the action bar above the two views, so the footer is handed no handler and draws none (decision 0612). */
            primary={quotePrimary}
            primaryDisabledReason={typeof facts.primaryDisabledReason === 'string' ? facts.primaryDisabledReason : null}
            sendAction={facts.sendAction === 'submitForApproval' || facts.sendAction === 'send' ? facts.sendAction : null}
            sendHint={typeof facts.sendHint === 'string' ? facts.sendHint : null}
            offersDecline={facts.offersDecline === true}
            offersVoid={facts.offersVoid === true}
            expiryDate={typeof answer.editable?.expiryDate === 'string' ? answer.editable.expiryDate : null}
            apiBaseUrl={apiBaseUrl}
            onDone={(message) => saved(message)}
            onError={failed}
            onOpenQuote={(id) => navigate(`${LIST_PAGES.Quote.route}/${id}`)}
            convertedSalesOrder={convertedSalesOrder}
            // 🔒 Convert is ticked in the accept window wherever the conversion would go through: the acceptance wins a working Deal, so only a Lost one draws no tick (decisions 1714, 2091).
            offersConvert={facts.dealStage !== 'Lost'}
            onOpenSalesOrder={(id) => navigate(`${LIST_PAGES.SalesOrder.route}/${id}`)}
            previousDelivery={answer.delivery ?? null}
            timeZone={timeZone}
            staleSend={answer.staleSend ?? null}
            onChangeDate={() => setEditOpen(true)}
            primaryRequest={primaryRequest}
            openFloors={openFloors}
            // 🔒 The accept window lists the optional lines, every one ticked to start (decision 2650).
            optionalLines={lines.filter((row) => row.isOptional).map((row) => ({ id: row.id, label: line(`${row.lineNumber}.`, row.description || row.itemLabel) }))}
            // 🔒 The account over its credit limit or on Credit Hold, named in the windows that convert (decision 3280).
            creditWarning={typeof facts.creditWarning === 'string' ? facts.creditWarning : null}
          />
        );
        const reviseAs = typeof facts.nextRevision === 'string' ? facts.nextRevision : null;
        /*
         * 🔒 The action bar's Edit, which is where every kind's now stands (decision
         * 0612). 🔨 A Draft edits as itself and a sent revision reopens as the next
         * one, and the control says which rather than asking afterwards (decision
         * 0474); the attributes are the ones the footer's control carried, so what
         * reads the Quote's Edit still finds it.
         */
        const editAction = facts.editable === true ? (
          <span data-quote-action="edit" data-quote-revises={reviseAs ?? ''}>
            <Button
              size={Size.Medium}
              color={Color.Navy}
              text={reviseAs ? [line(t('document.revise.action'), reviseAs)] : [t('document.edit.action')]}
              actions={[() => navigate(`${LIST_PAGES.Quote.route}/${answer.recordId}?mode=edit`)]}
            />
          </span>
        ) : null;
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        // An outside login's answer carries no notes tile, so neither view draws a panel (T337; decision 0952).
        const notesPanel = answer.tiles?.some((tile) => tile.key === 'internalNotes') ? (
          <DocumentNotesPanel
            recordType="Quote"
            recordId={answer.recordId}
            recordLabel={answer.title}
            rows={rowsOf(answer, 'internalNotes')}
            personId={personId}
            timeZone={timeZone}
            apiBaseUrl={apiBaseUrl}
            onChanged={page.reload}
            onError={failed}
          />
        ) : null;
        // One Files block, placed in the Details view and handed to the PDF view's rail as its Files tab (T1070; decision 1662).
        const filesPanel = (
          <section className={styles.block} data-quote-files={documentFilesFrom(rowsOf(answer, 'quoteFiles')).length}>
            <h2 className={styles.heading}>{t('documentFiles.title')}</h2>
            <DocumentFilesPanel recordType="Quote" recordId={answer.recordId} files={documentFilesFrom(rowsOf(answer, 'quoteFiles'))} leftInZoho={leftInZohoFrom(rowsOf(answer, 'quoteFiles'))} apiBaseUrl={apiBaseUrl} onChanged={page.reload} writeRefusal={answer.writeRefusal ?? null} />
          </section>
        );
        // One History panel, placed in the Details view and handed to the PDF view's rail as its History tab (T1070; decision 1662).
        const historyPanel = <DocumentHistoryPanel recordType="Quote" recordId={answer.recordId} rows={rowsOf(answer, 'history')} timeZone={timeZone} />;
        return (
          <DocumentViewPage
            notes={notesPanel}
            files={filesPanel}
            history={historyPanel}
            documentType="Quote"
            icon="quote"
            title={answer.title}
            subtitle={pageKindLineText({ page: 'document', documentType: 'Quote' })}
            information={answer.documentFacts ?? []}
            /* 🔨 The banner's own pencil corrects the dates, the terms and the display mode in place; the action bar's Edit reopens the whole form (decision 0409). */
            {...(EDIT_FIELDS.Quote.length > 0 ? { onCorrect: () => setEditOpen(true) } : {})}
            status={<BannerPills pills={pills} attribute="data-quote-pills" hint={typeof facts.approvalReason === 'string' ? facts.approvalReason : null} />}
            footer={footer}
            footerAttributes={{ 'data-quote-footer': quotePrimary ?? 'none' }}
            pageAttributes={{ 'data-quote': answer.recordId, 'data-quote-status': typeof facts.status === 'string' ? facts.status : '', 'data-quote-displayed-status': typeof facts.displayedStatus === 'string' ? facts.displayedStatus : '' }}
            print={quotePrintDocument(facts, lines)}
            layout={answer.printLayout ?? null}
            sheet={answer.printSheet ?? null}
            writeRefusal={answer.writeRefusal ?? null}
            /* 🔒 Decision 1225: a derived delivery member reads in its pill's words, so the ribbon never prints DELIVERYFAILED. */
            ribbon={held || facts.displayedStatus === 'Sending' || facts.displayedStatus === 'DeliveryFailed' ? (pills[0]?.text ?? null) : typeof facts.displayedStatus === 'string' ? facts.displayedStatus : null}
            ribbonStatus={held ? null : typeof facts.displayedStatus === 'string' ? facts.displayedStatus : null}
            ribbonGroup={held?.group ?? null}
            pdfPath={documentPdfPath(QUOTES_PATH, answer.recordId)}
            listRoute={LIST_PAGES.Quote.route}
            {...(editAction === null ? {} : { editAction })}
            apiBaseUrl={apiBaseUrl}
            terms={{ documentId: answer.recordId, onSaved: page.reload }}
            whatsNext={
              /* 🔒 The footer's primary, named as the next step (decision 1732); a primary the gate holds is not offered. */
              <WhatsNextBanner
                documentType="Quote"
                status={typeof facts.status === 'string' ? facts.status : null}
                actions={quoteStep && !facts.primaryDisabledReason ? { [quoteStep]: () => setPrimaryRequest((n) => n + 1) } : {}}
              />
            }
            notices={
              <>
                {notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
                {/* 🔒 The approval block says why the Quote rests where it does (T131; decision 0713). */}
                <ApprovalReason key={answer.recordId} view={typeof facts.approvalReason === 'string' ? { reason: facts.approvalReason, sendAction: null, sendHint: null, standing: standingOf(facts.approvalStanding), routedAt: typeof facts.approvalRoutedAt === 'string' ? facts.approvalRoutedAt : null } : null} />
                {/*
                  * 🔒 The document floor on the approval block, with its one dialog (decision 1629): the approver writes
                  * the reason on a held Quote, and a person who may write the Quote does on any other.
                  */}
                <QuoteFloorLine
                  quoteId={answer.recordId}
                  floor={answer.documentFloor ?? null}
                  mayAcknowledge={facts.approvalStatus === 'Pending' ? answer.canApprove === true : !answer.writeRefusal && facts.editable !== false}
                  apiBaseUrl={apiBaseUrl}
                  onDone={(message) => saved(message)}
                  onError={failed}
                />
                {/* 🔒 One line and one dialog per line floor broken, each taking its own reason (T920; decision 1695). */}
                {(answer.lineFloors ?? []).map((floor) => (
                  <QuoteFloorLine
                    key={floor.lineId}
                    quoteId={answer.recordId}
                    lineId={floor.lineId}
                    floor={floor}
                    mayAcknowledge={facts.approvalStatus === 'Pending' ? answer.canApprove === true : !answer.writeRefusal && facts.editable !== false}
                    apiBaseUrl={apiBaseUrl}
                    onDone={(message) => saved(message)}
                    onError={failed}
                  />
                ))}
                {/* 🔒 The list behind the Quote changed after the revision went out (decision 1466). */}
                <QuoteBomChangeLine change={answer.bomChange} />
                {/* 🔒 Who accepted, from the emailed link or recorded by a person (decision 2650). */}
                <QuoteAcceptanceLine acceptance={answer.acceptance} optionalCount={lines.filter((row) => row.isOptional).length} />
              </>
            }
            details={
              <>
              <section className={styles.block} data-quote-lines-block={lines.length}>
                <h2 className={styles.heading}>{t('quote.lines.title')}</h2>
                <QuoteLines
                  lines={lines}
                  discountLevel={(facts.discountLevel === 'Line' ? 'Line' : 'Footer') as DiscountLevel}
                  editable={facts.editable !== false}
                  taxRows={answer.taxRows ?? []}
                  lineFloors={answer.lineFloors ?? []}
                  takenOptionalIds={answer.acceptance ? answer.acceptance.optionalLineIds : null}
                />
              </section>

              {/* 🔒 Where the newest send stands, one line per recipient with the time and the TLS mark (decisions 1225, 1226). */}
              <DeliveredPanel delivery={answer.delivery ?? null} timeZone={timeZone} />

              {/* 🔒 Every Send's frozen copy, with any two compared side by side (decision 0159). */}
              <section className={styles.block} data-quote-versions-block={(answer.versions ?? []).length}>
                <h2 className={styles.heading}>{t('quote.versions.title')}</h2>
                <QuoteVersions key={`versions-${answer.versions?.[0]?.versionNumber ?? 0}`} quoteId={answer.recordId} rows={answer.versions ?? []} apiBaseUrl={apiBaseUrl} onError={failed} />
              </section>

              {/*
                * 🔒 The customer's signature on this quote (piece 27; decisions 0009, 0519). The method is drawn
                * beside every row, never a generic Signed badge, and Record signed copy is the manual path.
                */}
              <section className={styles.block} data-quote-signatures="true">
                <h2 className={styles.heading}>{t('signature.title')}</h2>
                <SignaturesPanel rows={signatures.state.kind === 'ready' ? signatures.state.data.rows : []} onRecord={() => setSignedCopyOpen(true)} />
              </section>

              {/* 🔨 Unmounted when closed, so reopening it on another quote does not show the first one's draft. */}
              {signedCopyOpen ? (
                <RecordSignedCopyModal
                  key={answer.recordId}
                  documentType="Quote"
                  documentId={answer.recordId}
                  files={documentFilesFrom(rowsOf(answer, 'quoteFiles'))}
                  /* 🔨 A converted Quote offers no second conversion from the signed copy (T438; decision 1121). */
                  offersConvert={convertedSalesOrder === null}
                  apiBaseUrl={apiBaseUrl}
                  onCancel={() => setSignedCopyOpen(false)}
                  onDone={(message) => {
                    setSignedCopyOpen(false);
                    signatures.reload();
                    saved(message);
                  }}
                />
              ) : null}

              {/* 🔒 The files attached to this quote (decision 0457). */}
              {filesPanel}

              {/* 🔒 The Deal's team as it stood when this document was created (decision 0765). */}
              <DealTeamPanel team={answer.dealTeam} timeZone={timeZone} />

              {/* 🔒 An outside login's answer carries no notes tile, so it is drawn no panel, the way a refused notes read draws none (T337; decision 0952). */}
              {notesPanel}

              {/* 🔒 The mail sent about this quote and its link's opens (decisions 0162, 0791). */}
              <RecordMessagesPanel recordType="Quote" recordId={answer.recordId} timeZone={timeZone} apiBaseUrl={apiBaseUrl} />
              {historyPanel}
              </>
            }
          >
              {/* 🔨 Unmounted when closed, so reopening it on another quote does not show the first one's draft. */}
              {editOpen ? (
                <EditRecordModal
                  key={answer.recordId}
                  open
                  recordType="Quote"
                  recordId={answer.recordId}
                  initial={answer.editable ?? {}}
                  apiBaseUrl={apiBaseUrl}
                  onCancel={() => setEditOpen(false)}
                  onSaved={() => { setEditOpen(false); page.reload(); }}
                  onDeleted={() => navigate(LIST_PAGES.Quote.route)}
                />
              ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}

/**
 * The route element: the quote named in the path, inside the list-and-detail
 * frame (decision 0408). 🔨 The frame hands the record it picked, because the
 * pick and the address it writes are not the same instant; the path is the
 * fallback for the route mounted on its own.
 */
export function QuotePageRoute({ recordId, apiBaseUrl = '' }: { recordId?: string; apiBaseUrl?: string }) {
  const { id = '' } = useParams();
  const shell = useOutletContext<ShellOutletContext | undefined>();
  return <QuotePage quoteId={recordId ?? id} apiBaseUrl={apiBaseUrl} personId={shell?.personId ?? ''} timeZone={shell?.timeZone ?? 'UTC'} />;
}
